import { buildSWRCashRates, describeSWRCashModel, resolveSWRCashMode, SWR_CASH_MODEL_VERSION, SWR_CASH_DEFAULTS } from './swrCashModel';
import { normalRandom } from './mathUtils';

export const SWR_MACRO_MODE_FIXED = 'fixed';
export const SWR_MACRO_MODE_STOCHASTIC_EURO_V1 = 'stochastic-euro-v1';
export const SWR_MACRO_MODE_HISTORICAL_JOINT = 'historical-joint-v1';
export const SWR_MACRO_CALIBRATION_VERSION = 'ecb-euro-hicp-cash-2000-2025-v1';
export const SWR_CASH_YIELD_TAX_RATE = 0.26;
const CALIBRATION_REFERENCE_TAX_RATE = 0.15;

const MONTHS_PER_YEAR = 12;

// Frozen OLS VAR(1) calibration on monthly ECB data, January 2000-December 2025.
// State variables are monthly HICP log inflation and monthly gross cash log return.
export const SWR_MACRO_CALIBRATION = Object.freeze({
  sampleStart: '2000-01',
  sampleEnd: '2025-12',
  observations: 312,
  inflationSeries: 'ICP.M.U2.Y.000000.3.INX',
  cashRateSeries: 'MIR.M.U2.B.L22.F.R.A.2250.EUR.N',
  historicalMonthlyLogInflationMean: 0.001741637441440182,
  historicalMonthlyLogCashGrossMean: 0.0015981675326424472,
  historicalMonthlyLogCashNetMean: 0.0013611785669720496,
  historicalMonthlyLogRealCashSpread: -0.0003804588744681324,
  transition: [
    [0.4193188924880842, -0.09497275868594116],
    [0.010455227110427585, 0.9958267523383922],
  ],
  innovationCovariance: [
    [0.0000047911012663509815, 3.0547234847816285e-8],
    [3.0547234847816285e-8, 7.77597044616086e-9],
  ],
});

const annualRateToMonthlyLog = (annualRate) => (
  Math.log(Math.max(1 + (Number(annualRate) || 0), 0.000001)) / MONTHS_PER_YEAR
);

const monthlyLogToRate = (monthlyLogRate) => Math.exp(monthlyLogRate) - 1;

const getGrossCashMeanLog = (annualInflationRate) => {
  const targetNetLog = annualRateToMonthlyLog(annualInflationRate)
    + SWR_MACRO_CALIBRATION.historicalMonthlyLogRealCashSpread;
  const targetNetRate = monthlyLogToRate(targetNetLog);
  const targetGrossRate = targetNetRate > 0
    ? targetNetRate / (1 - CALIBRATION_REFERENCE_TAX_RATE)
    : targetNetRate;
  return Math.log(Math.max(1 + targetGrossRate, 0.000001));
};

const getCholesky = () => {
  const covariance = SWR_MACRO_CALIBRATION.innovationCovariance;
  const l00 = Math.sqrt(Math.max(covariance[0][0], 0));
  const l10 = l00 > 0 ? covariance[1][0] / l00 : 0;
  const l11 = Math.sqrt(Math.max(covariance[1][1] - l10 * l10, 0));
  return [l00, l10, l11];
};

export const resolveSWRMacroMode = (params = {}) => (
  params.historicalPortfolio?.enabled === true ? SWR_MACRO_MODE_HISTORICAL_JOINT
    : params.macroMode === SWR_MACRO_MODE_STOCHASTIC_EURO_V1
    ? SWR_MACRO_MODE_STOCHASTIC_EURO_V1
    : SWR_MACRO_MODE_FIXED
);

export const getSWRMacroAssumptions = (params = {}) => {
  const mode = resolveSWRMacroMode(params);
  return {
    mode,
    stochastic: mode !== SWR_MACRO_MODE_FIXED,
    inflationRegion: params.historicalPortfolio?.enabled ? params.historicalPortfolio.inflationRegion : 'EA',
    calibrationVersion: mode === SWR_MACRO_MODE_HISTORICAL_JOINT ? params.historicalPortfolio.datasetVersion
      : mode === SWR_MACRO_MODE_STOCHASTIC_EURO_V1
      ? SWR_MACRO_CALIBRATION_VERSION
      : 'fixed-user-assumptions',
    expectedLongRunInflation: Number(params.inflationRate) || 0,
    cashReturnMode: resolveSWRCashMode(params),
    cashModelVersion: SWR_CASH_MODEL_VERSION,
    cashModelDescription: describeSWRCashModel(params),
    cashYieldTaxRate: resolveSWRCashMode(params) === 'inflation-net' ? 0 : (params.cashTaxRate ?? SWR_CASH_YIELD_TAX_RATE),
    cashTaxTreatment: resolveSWRCashMode(params) === 'inflation-net' ? 'included-in-net-assumption' : 'monthly-provision-positive-yield',
    cashRateParameters: Object.fromEntries(Object.entries(SWR_CASH_DEFAULTS).map(([key, fallback]) => [key, params[key] ?? fallback])),
    initialState: mode === SWR_MACRO_MODE_HISTORICAL_JOINT ? 'sampled-joint-historical-block' : 'long-run-equilibrium',
    marketCorrelation: mode === SWR_MACRO_MODE_HISTORICAL_JOINT ? 'same-historical-months-and-blocks' : 'independent-from-aggregate-portfolio',
    cashProxy: resolveSWRCashMode(params) === 'macro-proxy' ? 'euro-area-household-deposits-up-to-one-year-configurable-tax' : resolveSWRCashMode(params),
  };
};

const buildContext = ({
  mode,
  preStartMonths,
  simulationMonths,
  centralPreSimulationFactor,
  inflationMonthlyRates,
  grossCashMonthlyRates,
  netCashMonthlyRates,
}) => {
  const totalMonths = preStartMonths + simulationMonths;
  const priceIndices = new Float64Array(totalMonths + 1);
  priceIndices[0] = 1;
  for (let month = 0; month < totalMonths; month++) {
    priceIndices[month + 1] = priceIndices[month] * (1 + inflationMonthlyRates[month]);
  }

  const safeCentralFactor = Math.max(Number(centralPreSimulationFactor) || 1, 0.000001);
  const preSimulationCpi = priceIndices[preStartMonths];
  const getFullMonthIndex = (simulationMonthIndex) => Math.max(
    0,
    Math.min(preStartMonths + simulationMonthIndex, totalMonths)
  );
  const getSimulationRateIndex = (simulationMonthIndex) => Math.max(
    0,
    Math.min(preStartMonths + simulationMonthIndex, totalMonths - 1)
  );

  return {
    mode,
    preStartMonths,
    simulationMonths,
    preSimulationCpi,
    preSimulationScaleVsCentral: preSimulationCpi / safeCentralFactor,
    inflationMonthlyRates,
    grossCashMonthlyRates,
    netCashMonthlyRates,
    priceIndices,
    getIndex: (simulationMonthIndex) => (
      priceIndices[getFullMonthIndex(simulationMonthIndex)] / safeCentralFactor
    ),
    getTodayIndex: (simulationMonthIndex) => priceIndices[getFullMonthIndex(simulationMonthIndex)],
    getMonthlyRate: (simulationMonthIndex) => (
      inflationMonthlyRates[getSimulationRateIndex(simulationMonthIndex)] || 0
    ),
    getGrossCashRate: (simulationMonthIndex) => (
      grossCashMonthlyRates[getSimulationRateIndex(simulationMonthIndex)] || 0
    ),
    getNetCashRate: (simulationMonthIndex) => (
      netCashMonthlyRates[getSimulationRateIndex(simulationMonthIndex)] || 0
    ),
    getCashTaxRateImpact: (simulationMonthIndex) => {
      const index = getSimulationRateIndex(simulationMonthIndex);
      return Math.max((grossCashMonthlyRates[index] || 0) - (netCashMonthlyRates[index] || 0), 0);
    },
  };
};

export function buildSWRMacroContext(params = {}, random = null, annualInflationOverrides = null) {
  const mode = resolveSWRMacroMode(params);
  if (mode === SWR_MACRO_MODE_HISTORICAL_JOINT) throw new Error('Il modello storico richiede mercato e inflazione dello stesso percorso.');
  const years = Math.max(0, Math.round(Number(params.yearsToSimulate) || 0));
  const yearsUntilStart = params.planningMode === 'future' || params.futurePlanningEnabled
    ? Math.max(0, Math.round(Number(params.yearsUntilStart) || 0))
    : 0;
  const preStartMonths = yearsUntilStart * MONTHS_PER_YEAR;
  const simulationMonths = years * MONTHS_PER_YEAR;
  const totalMonths = preStartMonths + simulationMonths;
  const inflationRate = Number(params.inflationRate) || 0;
  const fixedCashMonthlyRate = monthlyLogToRate(annualRateToMonthlyLog(params.cashAnnualReturn ?? 0));
  const centralPreSimulationFactor = params.preSimulationInflationFactor
    || Math.pow(Math.max(1 + inflationRate, 0.000001), yearsUntilStart);
  const inflationMonthlyRates = new Float64Array(totalMonths);
  const grossCashMonthlyRates = new Float64Array(totalMonths);
  const finishContext = () => {
    const cash = buildSWRCashRates(params, inflationMonthlyRates, grossCashMonthlyRates);
    return buildContext({ mode, preStartMonths, simulationMonths, centralPreSimulationFactor,
      inflationMonthlyRates, grossCashMonthlyRates: cash.grossRates, netCashMonthlyRates: cash.netRates });
  };

  if (mode === SWR_MACRO_MODE_FIXED) {
    const monthlyInflationLog = annualRateToMonthlyLog(inflationRate);
    const monthlyInflationRate = monthlyLogToRate(monthlyInflationLog);
    for (let month = 0; month < totalMonths; month++) {
      const simulationYear = Math.floor((month - preStartMonths) / MONTHS_PER_YEAR) + 1;
      const override = month >= preStartMonths ? annualInflationOverrides?.[simulationYear] : null;
      const effectiveRate = override == null
        ? monthlyInflationRate
        : monthlyLogToRate(annualRateToMonthlyLog(override));
      inflationMonthlyRates[month] = effectiveRate;
      grossCashMonthlyRates[month] = fixedCashMonthlyRate;
    }
    return finishContext();
  }

  if (typeof random !== 'function') {
    throw new Error('Il modello macro stocastico richiede un generatore casuale deterministico.');
  }

  const meanInflationLog = annualRateToMonthlyLog(inflationRate);
  const meanGrossCashLog = getGrossCashMeanLog(inflationRate);
  const transition = SWR_MACRO_CALIBRATION.transition;
  const [l00, l10, l11] = getCholesky();
  let inflationDeviation = 0;
  let cashDeviation = 0;
  let stressCashDeviation = 0;

  for (let month = 0; month < totalMonths; month++) {
    const z0 = normalRandom(random, 0, 1);
    const z1 = normalRandom(random, 0, 1);
    const nextInflationDeviation = transition[0][0] * inflationDeviation
      + transition[0][1] * cashDeviation
      + l00 * z0;
    const nextCashDeviation = transition[1][0] * inflationDeviation
      + transition[1][1] * cashDeviation
      + l10 * z0
      + l11 * z1;
    inflationDeviation = nextInflationDeviation;
    cashDeviation = nextCashDeviation;

    const baseInflationLog = meanInflationLog + inflationDeviation;
    const baseGrossCashLog = meanGrossCashLog + cashDeviation;
    const simulationYear = Math.floor((month - preStartMonths) / MONTHS_PER_YEAR) + 1;
    const override = month >= preStartMonths ? annualInflationOverrides?.[simulationYear] : null;
    const floorInflationLog = override == null ? null : annualRateToMonthlyLog(override);
    const effectiveInflationLog = floorInflationLog == null
      ? baseInflationLog
      : Math.max(baseInflationLog, floorInflationLog);
    // Propagate an imposed inflation shock with the calibrated lag, not an instant mean shift.
    // Keep the underlying random CPI path paired to the baseline outside the stress window.
    const grossCashLog = baseGrossCashLog + stressCashDeviation;
    stressCashDeviation = transition[1][1] * stressCashDeviation
      + transition[1][0] * (effectiveInflationLog - baseInflationLog);
    const grossCashRate = monthlyLogToRate(grossCashLog);

    inflationMonthlyRates[month] = monthlyLogToRate(effectiveInflationLog);
    grossCashMonthlyRates[month] = grossCashRate;
  }

  return finishContext();
}

export function buildSWRMacroContextFromHistory(params, path, annualInflationOverrides = null) {
  const preStartMonths = path.preStartMonths;
  const simulationMonths = params.yearsToSimulate * MONTHS_PER_YEAR;
  const totalMonths = preStartMonths + simulationMonths;
  if (path.inflationRates.length !== totalMonths || path.grossCashRates.length !== totalMonths) {
    throw new Error('Le serie macro storiche non corrispondono all’orizzonte del percorso.');
  }
  const inflationMonthlyRates = annualInflationOverrides ? Float64Array.from(path.inflationRates) : path.inflationRates;
  for (let month = preStartMonths; month < totalMonths; month++) {
    const year = Math.floor((month - preStartMonths) / 12) + 1;
    const override = annualInflationOverrides?.[year];
    if (override != null) inflationMonthlyRates[month] = Math.expm1(Math.log1p(override) / 12);
  }
  const cash = buildSWRCashRates(params, inflationMonthlyRates, path.grossCashRates);
  return buildContext({ mode: SWR_MACRO_MODE_HISTORICAL_JOINT, preStartMonths, simulationMonths,
    centralPreSimulationFactor: params.preSimulationInflationFactor || 1,
    inflationMonthlyRates, grossCashMonthlyRates: cash.grossRates, netCashMonthlyRates: cash.netRates });
}
